<template>
    <ContentLoader v-slot :loading="playlist == null" class="playlist-detail">
        <section class="playlist-detail__hero">
            <div class="playlist-detail__cover-wrap">
                <div class="playlist-detail__cover-backdrop" :style="{ backgroundImage: `url('${coverImage}')` }"></div>
                <div class="playlist-detail__cover-card">
                    <img :src="coverImage" :alt="playlist.name" class="playlist-detail__cover">
                </div>
            </div>

            <div class="playlist-detail__main">
                <div class="playlist-detail__label">{{ $tt('播放列表') }}</div>
                <h1 class="playlist-detail__title">{{ playlist.name }}</h1>
                <p class="playlist-detail__summary">{{ playlistSummary }}</p>

                <div class="playlist-detail__chips">
                    <span class="playlist-detail__chip playlist-detail__chip--strong">
                        {{ $tt('{count} 首歌曲', { count: trackCount }) }}
                    </span>
                    <span class="playlist-detail__chip">{{ totalDuration }}</span>
                    <span class="playlist-detail__chip">{{ statusText }}</span>
                </div>

                <div class="playlist-detail__meta-grid">
                    <div class="playlist-detail__meta-item">
                        <span>{{ $tt('创建者') }}</span>
                        <strong>{{ playlist.owner || $tt('未知') }}</strong>
                    </div>
                    <div class="playlist-detail__meta-item">
                        <span>{{ $tt('创建时间') }}</span>
                        <strong>{{ formattedCreatedAt }}</strong>
                    </div>
                    <div class="playlist-detail__meta-item">
                        <span>{{ $tt('最近更新') }}</span>
                        <strong>{{ formattedUpdatedAt }}</strong>
                    </div>
                </div>

                <div class="playlist-detail__actions">
                    <button class="playlist-detail__play-button" @click="playNow(playlist.id)">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l10-6.86a1 1 0 0 0 0-1.72l-10-6.86A1 1 0 0 0 8 5.14z"></path>
                        </svg>
                        <span>{{ $tt('播放全部') }}</span>
                    </button>

                    <div class="drawer drawer-end playlist-detail__edit-drawer">
                        <input id="my-drawer-4" type="checkbox" class="drawer-toggle" />
                        <div class="drawer-content">
                            <label for="my-drawer-4" class="playlist-detail__secondary-button" @click="handleEdit">
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M5 18.08V21h2.92L18.5 10.42 15.58 7.5 5 18.08zM20.72 8.2a1 1 0 0 0 0-1.41l-3.51-3.51a1 1 0 0 0-1.41 0l-1.38 1.38 4.92 4.92 1.38-1.38z"></path>
                                </svg>
                                <span>{{ $tt('编辑') }}</span>
                            </label>
                        </div>
                        <div class="drawer-side">
                            <label for="my-drawer-4" aria-label="close sidebar" class="drawer-overlay"></label>
                            <div class="playlist-detail__drawer-panel">
                                <div class="playlist-detail__drawer-title">{{ $tt('编辑播放列表信息') }}</div>

                                <label class="playlist-detail__field">
                                    <span>{{ $tt('名称') }}</span>
                                    <input type="text" v-model="FormData.name" class="input input-bordered input-sm w-full" />
                                </label>

                                <div class="playlist-detail__field">
                                    <span>{{ $tt('封面') }}</span>
                                    <div v-if="reloadImg" class="relative">
                                        <div class="playlist-detail__upload">
                                            <div v-if="albumImagePreview" class="relative inline-block">
                                                <img :src="albumImagePreview" :alt="$tt('播放列表封面')" class="playlist-detail__upload-preview">
                                                <button
                                                    @click.stop="removeAlbumImage"
                                                    class="playlist-detail__upload-action playlist-detail__upload-action--remove"
                                                    type="button"
                                                >
                                                    ×
                                                </button>
                                                <button
                                                    @click.stop="triggerAlbumImageReplacement"
                                                    class="playlist-detail__upload-action playlist-detail__upload-action--replace"
                                                    type="button"
                                                    :title="$tt('替换图片')"
                                                >
                                                    +
                                                </button>
                                            </div>
                                            <div v-else class="playlist-detail__upload-empty">
                                                <svg viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zM8.5 8.5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3zM19 18H5l4-5 3 3.6 2-2.6 5 4z"></path>
                                                </svg>
                                                <p>{{ $tt('点击上传或拖拽图片到此处') }}</p>
                                                <small>{{ $tt('支持 JPG, PNG, GIF (最大 5MB)') }}</small>
                                            </div>
                                            <input
                                                ref="albumImageInput"
                                                type="file"
                                                accept="image/*"
                                                @change="handleAlbumImageChange"
                                                class="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                                :disabled="isUploadingAlbumImage"
                                                v-show="!albumImagePreview"
                                            >
                                            <div v-if="isUploadingAlbumImage" class="playlist-detail__uploading">
                                                <span class="loading loading-spinner loading-md"></span>
                                                <p>{{ $tt('上传中...') }}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <label class="playlist-detail__field">
                                    <span>{{ $tt('描述') }}</span>
                                    <textarea class="textarea textarea-bordered w-full h-40" v-model="FormData.description"></textarea>
                                </label>

                                <button class="playlist-detail__drawer-save" @click="handleSave">{{ $tt('保存') }}</button>

                                <div class="playlist-detail__recommend-title">{{ $tt('封面推荐') }}</div>
                                <div class="playlist-detail__recommend-list">
                                    <button
                                        v-for="(item2, index) in uniqueTrackList"
                                        :key="index"
                                        class="playlist-detail__recommend-row"
                                        @click="handleCopy('playlist_img', item2.image)"
                                    >
                                        <img :src="item2.image" :alt="item2.album || item2.artist" class="playlist-detail__recommend-image">
                                        <span>
                                            <strong @click.stop="handleCopy('name', item2.artist)">{{ item2.artist }}</strong>
                                            <small @click.stop="handleCopy('description', item2.album)">{{ item2.album }}</small>
                                        </span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <button class="playlist-detail__secondary-button" @click="openDeleteModal">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M9 3h6l1 2h4v2H4V5h4l1-2zm1 6h2v8h-2V9zm4 0h2v8h-2V9zM7 9h2v8H7V9z"></path>
                        </svg>
                        <span>{{ $tt('删除') }}</span>
                    </button>

                    <div class="dropdown dropdown-end dropdown-top playlist-detail__more" ref="selectContainer">
                        <button tabindex="0" type="button" class="playlist-detail__icon-button" @click="toggleDropdown">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M6 10a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm6 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm6 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"></path>
                            </svg>
                        </button>
                        <ul
                            v-if="isDropdownOpen"
                            tabindex="0"
                            class="dropdown-content menu playlist-detail__menu bottom-full mb-2"
                            @click.stop=""
                            @blur="closeDropdown"
                        >
                            <li>
                                <a @click.stop="togglePinnedPlaylist">
                                    <svg :class="['w-4 h-4', isPinnedPlaylist(playlist.id) ? 'text-primary' : 'fill-current']" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M14 2l8 8-2 2-1.6-1.6-4.8 4.8.4 4.8-1.4 1.4-4.2-4.2-4.8 4.8-1.4-1.4 4.8-4.8-4.2-4.2 1.4-1.4 4.8.4 4.8-4.8L12 4l2-2z" />
                                    </svg>
                                    {{ isPinnedPlaylist(playlist.id) ? $tt('取消钉住') : $tt('钉住到侧栏') }}
                                </a>
                            </li>
                            <li v-if="isMiPlay">
                                <a @click.stop="playInMi(playlist.id)">
                                    <svg-icon name="sys-xiaoai" class="w-4 h-4 fill-current" />
                                    {{ $tt('小爱音箱播放') }}
                                </a>
                            </li>
                            <li>
                                <a @click.stop="exportPlaylist(playlist.id)">
                                    <svg-icon name="export" class="w-4 h-4 fill-current" />
                                    {{ $tt('导出播放列表') }}
                                </a>
                            </li>
                            <li>
                                <a @click.stop="handleEditSmartPlaylist">
                                    <svg-icon name="edit" class="w-4 h-4 fill-current" />
                                    {{ $tt('编辑智能歌单规则') }}
                                </a>
                            </li>
                            <li>
                                <a @click.stop="updatePlaylistProps(playlist.id, 'exclude')">
                                    <svg-icon name="filter" class="w-4 h-4 fill-current" />
                                    {{ $tt('标记为过滤列表') }}
                                </a>
                            </li>
                            <li>
                                <a @click.stop="duplicatePlaylist(playlist.id)">
                                    <svg-icon name="player-repeat" class="w-4 h-4 fill-current" />
                                    {{ $tt('列表去重复') }}
                                </a>
                            </li>
                            <li>
                                <a @click.stop="handleShare">
                                    <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                        <circle cx="18" cy="5" r="3"></circle>
                                        <circle cx="6" cy="12" r="3"></circle>
                                        <circle cx="18" cy="19" r="3"></circle>
                                        <path d="M8.59 13.51 15.42 17.49"></path>
                                        <path d="M15.41 6.51 8.59 10.49"></path>
                                    </svg>
                                    {{ $tt('分享播放列表') }}
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>

        <section class="playlist-detail__tracks">
            <div class="playlist-detail__tracks-header">
                <div>
                    <h2>{{ $tt('歌曲列表') }}</h2>
                    <p>{{ $tt('按顺序整理这张播放列表里的声音') }}</p>
                </div>
                <span>{{ $tt('{count} 首', { count: trackCount }) }}</span>
            </div>
            <TrackList
                v-if="trackCount > 0"
                :tracks="playlist.tracks"
                :index-field="'index'"
                :draggable="true"
                :show-image="true"
            />
            <div v-else class="playlist-detail__empty">
                <div class="playlist-detail__empty-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 3v11.55A4 4 0 1 0 14 18V7h4V3h-6z"></path>
                    </svg>
                </div>
                <h2>{{ $tt('这里还没有歌曲') }}</h2>
                <p>{{ $tt('添加歌曲后，会在这里形成可播放的列表。') }}</p>
            </div>
        </section>
        <dialog id="playlist_delete" ref="playlistDeleteModal" class="modal modal-bottom sm:modal-middle">
            <div class="modal-box">
                <h3 class="text-lg font-bold">{{ $tt('删除播放列表') }}</h3>
                <div class="py-4 text-gray-400">
                    <div>{{ $tt('删除音乐: 将播放列表从音乐收藏中移除，同时删除列表内的音乐文件。') }}</div>
                    <div>{{ $tt('删除收藏: 将播放列表从音乐收藏中移除，但并不会删除音乐文件。') }}</div>
                </div>
                <div class="modal-action">
                    <form method="dialog">
                        <!-- if there is a button in form, it will close the modal -->
                        <button class="btn btn-error text-white btn-sm mr-2" @click="deletePlaylist('delete_file')">{{ $tt('删除音乐') }}</button>
                        <button class="btn btn-neutral btn-sm mr-2" @click="deletePlaylist('delete_track')">{{ $tt('删除收藏') }}</button>
                        <button class="btn btn-sm">{{ $tt('取消') }}</button>
                    </form>
                </div>
            </div>
        </dialog>
        <dialog id="smart-playlist-edit" ref="smart-playlist-edit" class="modal">
            <div class="modal-box w-2/3 max-w-5xl max-h-[80vh] flex flex-col">
                <h3 class="text-lg font-bold sticky top-0 bg-base-100 z-10 pb-2">{{ $tt('编辑智能播放列表规则') }}</h3>
                <div class="py-4 flex-1 overflow-y-auto">
                    <div v-for="(item, index) in conditions" :key="'a' + index">
                        <div class="flex items-center">
                            <div v-if="index === 0">{{ $tt('满足条件') }}</div>
                            <div v-else>
                                <div class="btn btn-sm">{{ $tt('或') }}</div>
                                {{ $tt('满足条件') }}
                            </div>
                            <div class="ml-2 flex">
                                <button class="btn btn-square btn-outline btn-sm mr-1"
                                    @click="addItem">+</button>
                                <button class="btn btn-square btn-outline btn-sm"
                                    @click="removeItem(index)" v-if="conditions.length > 1">-</button>
                            </div>
                        </div>
                        <div class="flex mt-2 ml-6 items-center" v-for="(sub, subIndex) in item"
                            :key="'s' + index + 'a' + subIndex">
                            <div v-if="subIndex >= 1" class="mr-1 btn btn-sm">{{ $tt('且') }}</div>
                            <single-select
                                v-model="sub.key"
                                :options="keyOptions"
                                class="mr-1"
                            />
                            <single-select
                                v-if="boolKeyList.indexOf(sub.key) !== -1"
                                v-model="sub.operate"
                                :options="operateOptions"
                                class="mr-1"
                            />
                            <single-select
                                v-else-if="numberKeyList.indexOf(sub.key) !== -1"
                                v-model="sub.operate"
                                :options="operateGteOptions"
                                class="mr-1"
                            />
                            <single-select
                                v-else
                                v-model="sub.operate"
                                :options="valueOptions"
                                class="mr-1"
                            />
                            <select class="select select-bordered select-sm w-full max-w-xs"
                                v-if="boolKeyList.indexOf(sub.key) !== -1" v-model="sub.value">
                                <option value="">True</option>
                            </select>
                            <div class="w-full max-w-xs flex items-center"
                                v-else-if="numberKeyList.indexOf(sub.key) !== -1">
                                <input type="number" :placeholder="$tt('请输入')"
                                    class="input input-bordered input-sm w-full max-w-xs"
                                    v-model="sub.value" />
                                <div class="ml-2" v-if="sub.key === 'bit-rate'">kps</div>
                                <div class="ml-2" v-else-if="sub.key === 'size'">MB</div>
                                                <div class="ml-2" v-else-if="['play-count', 'album-play-count', 'artist-play-count'].includes(sub.key)">{{ $tt('次') }}</div>
                                <div class="ml-2" v-else-if="sub.key === 'year'">{{ $tt('年') }}</div>
                                <div class="ml-2" v-else-if="sub.key === 'duration'">{{ $tt('秒') }}</div>
                                <div class="ml-2" v-else-if="sub.key === 'accessed-date'">{{ $tt('天') }}</div>
                                <div class="ml-2" v-else-if="sub.key === 'created-at'">{{ $tt('天') }}</div>
                                <div class="ml-2" v-else-if="sub.key === 'cover-width'">px</div>
                                <div class="ml-2" v-else-if="sub.key === 'cover-height'">px</div>
                                <div v-else></div>
                            </div>
                            <div class="w-full max-w-xs" v-else>
                                <input type="text" :placeholder="$tt('请输入')"
                                    class="input input-bordered input-sm w-full max-w-xs"
                                    v-model="sub.value" />
                            </div>
                            <div class="ml-2 flex">
                                <button class="btn btn-square btn-outline btn-sm mr-1"
                                    @click="addSubItem(index)">+</button>
                                <button class="btn btn-square btn-outline btn-sm"
                                    @click="removeSubItem(index, subIndex)"
                                    v-if="item.length > 1">-</button>
                            </div>
                        </div>
                        <div class="divider"
                            v-if="conditions.length > 1 && conditions.length !== index + 1">OR</div>
                    </div>
                    <div class="flex mt-4 items-center">
                        <div class="w-8 mr-1">{{ $tt('最多') }}</div>
                        <input type="number" min="1" :placeholder="$tt('请输入')"
                            class="input input-bordered input-sm w-16 max-w-xs mr-1" v-model.number="limit" />
                        <select class="select select-bordered select-sm w-16 max-w-xs mr-2"
                            v-model="unit">
                            <option value="item">{{ $tt('项') }}</option>
                        </select>
                        <div class="w-16 mr-1">{{ $tt('选择标准') }}</div>
                        <single-select
                            v-model="selectStandard"
                            :options="selectStandardOptions"
                        />
                    </div>
                    <div class="form-control w-24">
                        <label class="cursor-pointer label">
                            <input type="checkbox" class="checkbox checkbox-secondary"
                                v-model="isDynamic" />
                            <span class="label-text">{{ $tt('动态更新') }}</span>
                        </label>
                    </div>
                </div>
                <div class="modal-action sticky bottom-0 bg-base-100 z-10 pt-2">
                    <form method="dialog">
                        <button class="btn btn-neutral btn-sm mr-2" @click="handleUpdateSmartPlaylist">{{ $tt('保存') }}</button>
                        <button class="btn btn-sm">{{ $tt('取消') }}</button>
                    </form>
                </div>
            </div>
        </dialog>
        <!-- 分享对话框 -->
        <PlaylistShareModal ref="shareModal" :playlist-id="id" />
    </ContentLoader>
</template>
<script>
import TrackList from '@/views/library/track/TrackList'
import ContentLoader from '@/components/library/ContentLoader'
import SingleSelect from '@/components/iview/SingleSelect'
import SvgIcon from '@/components/SvgIcon.vue'
import PlaylistShareModal from '@/components/library/PlaylistShareModal.vue'
import {
    getPinnedPlaylistIds,
    togglePlaylistPinned
} from '@/utils/pinnedPlaylists'

const { getCoverArtUrl } = require('../../../api/apiUrl/subsonic/serializers')
export default {
    components: {
        TrackList, ContentLoader, SingleSelect, SvgIcon, PlaylistShareModal
    },
    props: {
    },
    data() {
        return {
            playlist: null,
            showEditModal: false,
            id: this.$route.params.id,
            uploadHeader: [
                { name: 'X-CSRFToken', value: this.getCookie('django_vue_cli_csrftoken') },
                { name: 'AUTHORIZATION', value: this.getCookie('AUTHORIZATION') }
            ],
            uploadUrl: '/apimt/upload_image/',
            FormData: {},
            artistsData: [],
            uniqueTrackList: [],
            isMiPlay: localStorage.getItem('mi_play_in_pl') === '1' || false,
            isDropdownOpen: false,
            // 图片上传相关数据
            albumImagePreview: null,
            isUploadingAlbumImage: false,
            reloadImg: true,
            pinnedPlaylistIds: getPinnedPlaylistIds(),
            // 智能歌单编辑相关数据
            conditions: [[
                { 'key': 'favourite-track', 'operate': 'is', 'value': '' }
            ]],
            limit: 25,
            unit: 'item',
            selectStandard: '?',
            isDynamic: false,
            boolKeyList: ['favourite-track', 'favourite-album', 'favourite-artist', 'album-img', 'artist-img'],
            numberKeyList: ['play-count', 'album-play-count', 'artist-play-count', 'sample-rate', 'bit-rate', 'size', 'year', 'duration', 'accessed-date', 'created-at', 'rating', 'cover-width', 'cover-height'],
            keyOptions: [
                { id: 'favourite-track', name: this.$tt('标记为喜爱的歌曲') },
                { id: 'favourite-album', name: this.$tt('标记为喜爱的专辑') },
                { id: 'favourite-artist', name: this.$tt('标记为喜爱的艺术家') },
                { id: 'rating', name: this.$tt('评分') },
                { id: 'file-path', name: this.$tt('文件路径') },
                { id: 'title', name: this.$tt('标题') },
                { id: 'album', name: this.$tt('专辑') },
                { id: 'artist', name: this.$tt('艺术家') },
                { id: 'genre', name: this.$tt('流派') },
                { id: 'comment', name: this.$tt('描述') },
                { id: 'lyrics', name: this.$tt('歌词') },
                { id: 'language', name: this.$tt('语言') },
                { id: 'album-img', name: this.$tt('专辑图片') },
                { id: 'artist-img', name: this.$tt('艺术家图片') },
                { id: 'play-count', name: this.$tt('播放次数') },
                { id: 'album-play-count', name: this.$tt('专辑播放次数') },
                { id: 'artist-play-count', name: this.$tt('艺术家播放次数') },
                { id: 'bit-rate', name: this.$tt('比特率') },
                { id: 'size', name: this.$tt('大小') },
                { id: 'year', name: this.$tt('年份') },
                { id: 'duration', name: this.$tt('时长') },
                { id: 'accessed-date', name: this.$tt('距离最近播放') },
                { id: 'created-at', name: this.$tt('距离最近添加') },
                { id: 'cover-width', name: this.$tt('封面宽度') },
                { id: 'cover-height', name: this.$tt('封面高度') },
            ],
            operateOptions: [
                { id: 'is', name: this.$tt('是') },
                { id: 'not', name: this.$tt('不是') },
            ],
            operateGteOptions: [
                { id: 'gte', name: this.$tt('大于') },
                { id: 'lte', name: this.$tt('小于') },
                { id: 'is', name: this.$tt('是') },
                { id: 'not', name: this.$tt('不是') },
            ],
            valueOptions: [
                { id: 'contain', name: this.$tt('包含') },
                { id: 'not_contain', name: this.$tt('不包含') },
                { id: 'startwith', name: this.$tt('开头是') },
                { id: 'endwith', name: this.$tt('结尾是') },
                { id: 'is', name: this.$tt('是') },
                { id: 'not', name: this.$tt('不是') },
            ],
            selectStandardOptions: [
                { id: '?', name: this.$tt('随机') },
                { id: '-accessed_date', name: this.$tt('最近播放') },
                { id: 'accessed_date', name: this.$tt('最早播放') },
                { id: '-plays_count', name: this.$tt('最常播放') },
                { id: 'plays_count', name: this.$tt('最少播放') },
                { id: '-created_at', name: this.$tt('最近添加') },
                { id: 'created_at', name: this.$tt('最早添加') },
            ],
            isMobile: false
        }
    },
    computed: {
        coverImage() {
            if (this.playlist && this.playlist.image) {
                return this.formatImageUrl(this.playlist.image)
            }
            return '/static/dist/img/album_null.jpg'
        },
        trackCount() {
            return this.playlist && this.playlist.tracks ? this.playlist.tracks.length : 0
        },
        totalDuration() {
            const total = (this.playlist && this.playlist.tracks ? this.playlist.tracks : [])
                .reduce((sum, item) => sum + (Number(item.duration) || 0), 0)
            return this.formatDuration(total)
        },
        playlistSummary() {
            if (this.playlist && this.playlist.comment) {
                return this.playlist.comment
            }
            return this.$tt('这张播放列表还没有简介，先从歌曲本身开始听。')
        },
        statusText() {
            if (!this.playlist) {
                return '--'
            }
            if (this.playlist.privacyLevel === 'exclude') {
                return this.$tt('过滤歌单')
            }
            if (this.playlist.isDynamic) {
                return this.$tt('动态歌单')
            }
            return this.$tt('普通歌单')
        },
        formattedCreatedAt() {
            return this.formatPlaylistDate(this.playlist && this.playlist.createdAt)
        },
        formattedUpdatedAt() {
            return this.formatPlaylistDate(this.playlist && this.playlist.updatedAt)
        }
    },
    async created() {
        this.playlist = await this.$api.subsonic.getPlaylist(this.id)
        this.FormData = {
            name: this.playlist.name,
            description: this.playlist.comment,
            playlist_img: this.playlist.image ? this.playlist.image : ''
        }
        this.uniqueTrackList = this.playlist.tracks.reduce((unique, item) => {
            // 判断当前元素的 id 是否已经存在于 unique 列表中
            if (!unique.some(elem => elem.album === item.album)) {
                unique.push(item)
            }
            return unique
        }, [])
        // 初始化图片预览，使用工具方法格式化
        this.albumImagePreview = this.formatImageUrl(this.playlist.image)
    },
    mounted() {
        this.checkMobile()
        window.addEventListener('resize', this.checkMobile)
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.checkMobile)
    },
    methods: {
        formatDuration(value) {
            if (!isFinite(value)) {
                return '--'
            }
            const totalSeconds = Math.floor(value)
            const hours = Math.floor(totalSeconds / 3600)
            const minutes = Math.floor((totalSeconds % 3600) / 60)
            const seconds = totalSeconds % 60
            if (hours > 0) {
                return `${hours}:${`${minutes}`.padStart(2, '0')}:${`${seconds}`.padStart(2, '0')}`
            }
            return `${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`
        },
        formatPlaylistDate(value) {
            if (!value) {
                return '--'
            }
            const date = new Date(value)
            if (Number.isNaN(date.getTime())) {
                return value
            }
            const year = date.getFullYear()
            const month = `${date.getMonth() + 1}`.padStart(2, '0')
            const day = `${date.getDate()}`.padStart(2, '0')
            return `${year}-${month}-${day}`
        },
        async playNow(id) {
            const playlist = await this.$api.subsonic.getPlaylist(id)
            return this.$store.dispatch('playTrackList', {
                tracks: playlist.tracks
            })
        },
        isPinnedPlaylist(id) {
            return this.pinnedPlaylistIds.includes(String(id))
        },
        togglePinnedPlaylist() {
            if (!this.playlist) {
                return
            }
            togglePlaylistPinned(this.playlist.id)
            this.pinnedPlaylistIds = getPinnedPlaylistIds()
        },
        playInMi() {
            this.$api.Task.miServicePlayList({ 'pl_id': this.id }).then((res) => {
                if (res.result) {
                    this.$myMsg.notify({
                        title: this.$tt('播放成功！'),
                        type: 'success',
                        time: 2000,
                    })
                } else {
                    this.$myMsg.notify({
                        title: this.$tt('播放失败！'),
                        type: 'error',
                        time: 2000,
                    })
                }
            })
        },
        removeTrack(index) {
            this.playlist.tracks.splice(index, 1)
            return this.playlistStore.removeTrack(this.id, index)
        },
        updatePlaylist(value) {
            this.playlist = value
            return this.playlistStore.update(this.playlist)
        },
        deletePlaylist(mode) {
            this.$api.subsonic.deletePlaylist(this.id, mode).then((res) => {
                this.$myMsg.notify({
                    title: this.$tt('删除成功！'),
                    type: 'success',
                    time: 2000,
                })
                this.$router.push({ path: '/playlists/recently-added/' })
            })
        },
        openDeleteModal() {
            if (this.$refs.playlistDeleteModal) {
                this.$refs.playlistDeleteModal.showModal()
            }
        },
        handleEdit() {
            this.showEditModal = true
            this.genrePlaylistCoverFuc()
        },
        handleRes(response) {
            if (response.result) {
                this.FormData.playlist_img = response.data
                return true
            } else {
                return false
            }
        },
        handleCopy(k, v) {
            if (k === 'playlist_img') {
                this.FormData.playlist_img = v
                // 使用工具方法格式化图片URL
                this.albumImagePreview = this.formatImageUrl(v)
            } else {
                this.FormData[k] += ',' + v
            }
        },
        handleSave() {
            this.$api.Task.putPlayList({ id: this.id, ...this.FormData }).then((res) => {
                if (res.result) {
                    this.$myMsg.notify({
                        title: this.$tt('修改成功！'),
                        type: 'success',
                        time: 2000,
                    })
                    this.$api.subsonic.getPlaylist(this.id).then((res) => {
                        this.playlist = res
                    })
                }
            })
        },
        genrePlaylistCoverFuc() {
            this.$api.Task.genrePlaylistCover({ 'id': this.id }).then((res) => {
                if (res.result) {
                    const img = getCoverArtUrl({ 'coverArt': res.data.coverArt })
                    if (this.uniqueTrackList && this.uniqueTrackList[0].coverArt === this.id) {
                        this.uniqueTrackList[0] = {
                            'image': img,
                            'artist': res.data.artist,
                            'album': res.data.album,
                            'coverArt': this.id
                        }
                    } else {
                        this.uniqueTrackList.unshift({
                            'image': img,
                            'artist': res.data.artist,
                            'album': res.data.album,
                            'coverArt': this.id
                        })
                    }
                }
            })
        },
        toggleDropdown() {
            this.isDropdownOpen = !this.isDropdownOpen;
        },
        closeDropdown() {
            this.isDropdownOpen = false;
        },
        exportPlaylist(id) {
            this.$api.subsonic.exportPlaylist(id).then((res) => {
                window.open(res.url, '_blank')
                console.log(res)
            })
        },
        updatePlaylistProps(id, privacy_level) {
            this.$api.subsonic.updatePlaylistProps(id, privacy_level).then((res) => {
                this.$myMsg.notify({
                    title: this.$tt('更新成功！'),
                    type: 'success',
                    time: 2000,
                })
            })
        },
        duplicatePlaylist(id) {
            this.$api.subsonic.duplicatePlaylist(id).then((res) => {
                this.$myMsg.notify({
                    title: this.$tt('去重成功！'),
                    type: 'success',
                    time: 2000,
                })
                this.$api.subsonic.getPlaylist(id).then((res) => {
                    this.playlist = res
                })
            })
        },
        getPlaylistDetail(playlistId) {
            return this.$api.Task.playlistDetail({ playlist_id: playlistId }).then((res) => {
                if (res.result) {
                    return res.data
                } else {
                    this.$myMsg.notify({
                        title: this.$tt('获取歌单详情失败'),
                        content: res.message || this.$tt('未知错误'),
                        type: 'error',
                        time: 2000,
                    })
                    return null
                }
            }).catch((error) => {
                this.$myMsg.notify({
                    title: this.$tt('获取歌单详情失败'),
                    content: this.$tt('网络错误'),
                    type: 'error',
                    time: 2000,
                })
                console.error(error)
                return null
            })
        },
        updatePlaylistCondition(playlistId, conditionDict) {
            return this.$api.Task.updatePlaylist({ 
                playlist_id: playlistId, 
                condition_dict: conditionDict,
                is_dynamic: this.isDynamic
            }).then((res) => {
                if (res.result) {
                    this.$myMsg.notify({
                        title: this.$tt('更新歌单规则成功！'),
                        type: 'success',
                        time: 2000,
                    })
                    return res.data
                } else {
                    this.$myMsg.notify({
                        title: this.$tt('更新歌单规则失败'),
                        content: res.message || this.$tt('未知错误'),
                        type: 'error',
                        time: 2000,
                    })
                    return null
                }
            }).catch((error) => {
                this.$myMsg.notify({
                    title: this.$tt('更新歌单规则失败'),
                    content: this.$tt('网络错误'),
                    type: 'error',
                    time: 2000,
                })
                console.error(error)
                return null
            })
        },
        // 移除专辑封面
        removeAlbumImage() {
            this.albumImagePreview = null
            this.FormData.playlist_img = ''
            if (this.$refs.albumImageInput) {
                this.$refs.albumImageInput.value = ''
            }
        },
        // 触发专辑封面替换
        triggerAlbumImageReplacement() {
            // 直接创建临时input，更可靠
            this.createTempFileInput('album')
        },
        
        // 创建临时文件输入
        createTempFileInput(type) {
            const input = document.createElement('input')
            input.type = 'file'
            input.accept = 'image/*'
            input.style.display = 'none'
            input.onchange = (event) => {
                if (type === 'album') {
                    this.handleAlbumImageChange(event)
                }
                // 清理临时元素
                if (input.parentNode) {
                    input.parentNode.removeChild(input)
                }
            }
            document.body.appendChild(input)
            input.click()
        },
        // 处理专辑封面图片选择
        handleAlbumImageChange(event) {
            const file = event.target.files[0]
            if (!file) return
            
            // 验证文件类型
            if (!file.type.startsWith('image/')) {
                this.$myMsg.notify({
                    title: this.$tt('文件类型错误'),
                    content: this.$tt('请选择图片文件'),
                    type: 'error',
                    time: 2000,
                })
                return
            }
            
            // 验证文件大小 (5MB)
            if (file.size > 5 * 1024 * 1024) {
                this.$myMsg.notify({
                    title: this.$tt('文件过大'),
                    content: this.$tt('图片大小不能超过5MB'),
                    type: 'error',
                    time: 2000,
                })
                return
            }
            
            // 显示预览
            const reader = new FileReader()
            reader.onload = (e) => {
                this.albumImagePreview = e.target.result
            }
            reader.readAsDataURL(file)
            
            // 上传图片
            this.uploadImage(file, 'album')
        },
        // 通用图片上传方法
        uploadImage(file, type) {
            const formData = new FormData()
            formData.append('upload_file', file)
            
            // 设置上传状态
            if (type === 'album') {
                this.isUploadingAlbumImage = true
            }
            
            // 创建 XMLHttpRequest
            const xhr = new XMLHttpRequest()
            
            // 设置请求头
            xhr.open('POST', this.uploadUrl, true)
            xhr.setRequestHeader('X-CSRFToken', this.getCookie('django_vue_cli_csrftoken'))
            xhr.setRequestHeader('AUTHORIZATION', this.getCookie('AUTHORIZATION'))
            xhr.withCredentials = true
            
            // 处理响应
            xhr.onload = () => {
                // 重置上传状态
                if (type === 'album') {
                    this.isUploadingAlbumImage = false
                }
                
                if (xhr.status === 200) {
                    try {
                        const response = JSON.parse(xhr.responseText)
                        if (this.handleRes(response)) {
                            this.$myMsg.notify({
                                title: this.$tt('上传成功'),
                                type: 'success',
                                time: 2000,
                            })
                        } else {
                            this.$myMsg.notify({
                                title: this.$tt('上传失败'),
                                content: response.message || this.$tt('未知错误'),
                                type: 'error',
                                time: 2000,
                            })
                        }
                    } catch (e) {
                        console.log('解析响应出错:', e, '原始响应:', xhr.responseText)
                        this.$myMsg.notify({
                            title: this.$tt('上传失败'),
                            content: this.$tt('服务器响应格式错误'),
                            type: 'error',
                            time: 2000,
                        })
                    }
                } else {
                    this.$myMsg.notify({
                        title: this.$tt('上传失败'),
                        content: this.$tt('HTTP错误: {status}', { status: xhr.status }),
                        type: 'error',
                        time: 2000,
                    })
                }
            }
            
            // 处理错误
            xhr.onerror = () => {
                if (type === 'album') {
                    this.isUploadingAlbumImage = false
                }
                this.$myMsg.notify({
                    title: this.$tt('上传失败'),
                    content: this.$tt('网络错误'),
                    type: 'error',
                    time: 2000,
                })
            }
            
            // 发送请求
            xhr.send(formData)
        },
        getCookie(name) {
            let cookieValue = null;
            if (document.cookie && document.cookie !== '') {
                const cookies = document.cookie.split(';');
                for (let i = 0; i < cookies.length; i++) {
                    const cookie = cookies[i].trim();
                    if (cookie.substring(0, name.length + 1) === (name + '=')) {
                        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                        break;
                    }
                }
            }
            return cookieValue;
        },
        // 格式化图片URL的工具方法
        formatImageUrl(imageData) {
            if (!imageData) return null;
            
            // 如果已经是data URL，直接返回
            if (imageData.startsWith('data:')) {
                return imageData;
            }
            
            // 如果是base64编码的图片数据（常见的开头）
            if (imageData.startsWith('/9j/') || imageData.startsWith('iVBOR') || imageData.startsWith('R0lGOD') || imageData.startsWith('UklGR')) {
                // 根据不同的开头判断图片类型
                let mimeType = 'image/jpeg'; // 默认
                if (imageData.startsWith('iVBOR')) {
                    mimeType = 'image/png';
                } else if (imageData.startsWith('R0lGOD')) {
                    mimeType = 'image/gif';
                } else if (imageData.startsWith('UklGR')) {
                    mimeType = 'image/webp';
                }
                return `data:${mimeType};base64,${imageData}`;
            }
            
            // 如果是普通URL，直接返回
            return imageData;
        },
        handleEditSmartPlaylist() {
            this.getPlaylistDetail(this.id).then((data) => {
                console.log(111, data)
                if (data && data.condition_dict) {
                    const conditionData = data.condition_dict
                    this.conditions = conditionData.conditions || [[
                        { 'key': 'favourite-track', 'operate': 'is', 'value': '' }
                    ]]
                    this.limit = conditionData.limit || 25
                    this.selectStandard = conditionData.order_by || '?'
                    this.isDynamic = data.is_dynamic
                } else {
                    this.conditions = [[
                        { 'key': 'favourite-track', 'operate': 'is', 'value': '' }
                    ]]
                    this.limit = 25
                    this.selectStandard = '?'
                }
                this.$refs["smart-playlist-edit"].showModal()
            })
        },
        addSubItem(index) {
            this.conditions[index].push({ 'key': '', 'operate': '', 'value': '' })
        },
        removeSubItem(index, subIndex) {
            if (this.conditions[index].length > 1) {
                this.conditions[index].splice(subIndex, 1)
            }
        },
        addItem() {
            this.conditions.push([{ 'key': '', 'operate': '', 'value': '' }])
        },
        removeItem(index) {
            this.conditions.splice(index, 1)
        },
        handleUpdateSmartPlaylist() {
            const conditionData = {
                conditions: this.conditions,
                limit: this.limit,
                order_by: this.selectStandard
            }
            this.updatePlaylistCondition(this.id, JSON.stringify(conditionData)).then((data) => {
                if (data !== null) {
                    this.$refs["smart-playlist-edit"].close()
                    this.$api.subsonic.getPlaylist(this.id).then((res) => {
                        this.playlist = res
                    })
                }
            })
        },
        // 检查是否为移动端
        checkMobile() {
            this.isMobile = window.innerWidth < 768 // md断点
        },
        // 打开分享对话框
        handleShare() {
            this.$refs.shareModal.show()
        }
    }
}
</script>

<style scoped>
.playlist-detail {
    --playlist-radius: 18px;
    --playlist-border: oklch(var(--bc) / 0.12);
    --playlist-muted: oklch(var(--bc) / 0.62);
    --playlist-soft: color-mix(in oklch, oklch(var(--b2)) 72%, transparent);
    height: calc(100vh - 5rem);
    min-height: 0;
    padding: 18px;
    box-sizing: border-box;
    border-radius: 1rem;
    overflow-y: auto;
    overflow-x: hidden;
    color: oklch(var(--bc));
    background:
        radial-gradient(circle at 12% 8%, color-mix(in srgb, oklch(var(--p)) 16%, transparent), transparent 28rem),
        radial-gradient(circle at 88% 4%, color-mix(in srgb, oklch(var(--a)) 12%, transparent), transparent 26rem),
        oklch(var(--b2));
}

.playlist-detail__hero {
    position: relative;
    display: grid;
    grid-template-columns: minmax(15rem, 22rem) minmax(0, 1fr);
    gap: 1.5rem;
    align-items: stretch;
    max-width: 88rem;
    margin: 0 auto;
    padding: 1.1rem;
    border-radius: 1.4rem;
    background: color-mix(in oklch, oklch(var(--b1)) 88%, transparent);
    box-shadow: 0 18px 46px color-mix(in srgb, oklch(var(--bc)) 10%, transparent);
    overflow: hidden;
}

.playlist-detail__cover-wrap {
    position: relative;
    min-height: 21rem;
    border-radius: 1.15rem;
    overflow: hidden;
    background: oklch(var(--b2));
}

.playlist-detail__cover-backdrop {
    position: absolute;
    inset: -1.5rem;
    background-position: center;
    background-size: cover;
    filter: blur(34px) saturate(1.15);
    opacity: 0.32;
    transform: scale(1.08);
}

.playlist-detail__cover-card {
    position: absolute;
    inset: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.75rem;
    border-radius: 1.1rem;
    background: color-mix(in oklch, oklch(var(--b1)) 72%, transparent);
    box-shadow: inset 0 1px 0 color-mix(in srgb, white 42%, transparent);
}

.playlist-detail__cover {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 0.9rem;
    box-shadow: 0 18px 42px color-mix(in srgb, oklch(var(--bc)) 18%, transparent);
}

.playlist-detail__main {
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 0.85rem 0.55rem 0.85rem 0;
}

.playlist-detail__label {
    width: fit-content;
    margin-bottom: 0.7rem;
    padding: 0.35rem 0.75rem;
    border-radius: 999px;
    background: color-mix(in oklch, oklch(var(--b2)) 86%, transparent);
    color: color-mix(in oklch, oklch(var(--bc)) 62%, transparent);
    font-size: 0.78rem;
    font-weight: 800;
}

.playlist-detail__title {
    max-width: 13ch;
    margin: 0;
    color: oklch(var(--bc));
    font-size: clamp(2.4rem, 5vw, 4.7rem);
    line-height: 0.96;
    font-weight: 850;
    letter-spacing: -0.04em;
    word-break: break-word;
}

.playlist-detail__summary {
    max-width: 62ch;
    margin: 1rem 0 0;
    color: color-mix(in oklch, oklch(var(--bc)) 66%, transparent);
    font-size: 0.98rem;
    line-height: 1.75;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.playlist-detail__chips,
.playlist-detail__actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.65rem;
}

.playlist-detail__chips {
    margin-top: 1.15rem;
}

.playlist-detail__chip {
    min-height: 2.1rem;
    display: inline-flex;
    align-items: center;
    padding: 0.45rem 0.85rem;
    border-radius: 999px;
    background: color-mix(in oklch, oklch(var(--b2)) 84%, transparent);
    color: color-mix(in oklch, oklch(var(--bc)) 72%, transparent);
    font-size: 0.82rem;
    font-weight: 750;
    white-space: nowrap;
}

.playlist-detail__chip--strong {
    background: oklch(var(--bc));
    color: oklch(var(--b1));
}

.playlist-detail__meta-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.75rem;
    margin-top: 1.25rem;
}

.playlist-detail__meta-item {
    min-width: 0;
    padding: 0.85rem;
    border-radius: 1rem;
    background: color-mix(in oklch, oklch(var(--b2)) 78%, transparent);
}

.playlist-detail__meta-item span,
.playlist-detail__meta-item strong {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.playlist-detail__meta-item span {
    color: color-mix(in oklch, oklch(var(--bc)) 55%, transparent);
    font-size: 0.76rem;
    font-weight: 700;
}

.playlist-detail__meta-item strong {
    margin-top: 0.28rem;
    color: oklch(var(--bc));
    font-size: 0.92rem;
}

.playlist-detail__actions {
    margin-top: 1.35rem;
}

.playlist-detail__play-button,
.playlist-detail__secondary-button,
.playlist-detail__icon-button,
.playlist-detail__drawer-save {
    border: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: 800;
    transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}

.playlist-detail__play-button:active,
.playlist-detail__secondary-button:active,
.playlist-detail__icon-button:active,
.playlist-detail__drawer-save:active {
    transform: translateY(1px) scale(0.99);
}

.playlist-detail__play-button {
    min-height: 3rem;
    padding: 0 1.25rem;
    gap: 0.55rem;
    border-radius: 999px;
    background: oklch(var(--bc));
    color: oklch(var(--b1));
    box-shadow: 0 14px 28px color-mix(in srgb, oklch(var(--bc)) 18%, transparent);
}

.playlist-detail__secondary-button {
    min-height: 3rem;
    padding: 0 1rem;
    gap: 0.48rem;
    border-radius: 999px;
    background: color-mix(in oklch, oklch(var(--b1)) 90%, transparent);
    color: oklch(var(--bc));
    cursor: pointer;
    box-shadow: 0 10px 22px color-mix(in srgb, oklch(var(--bc)) 8%, transparent);
}

.playlist-detail__icon-button {
    width: 3rem;
    height: 3rem;
    border-radius: 999px;
    background: color-mix(in oklch, oklch(var(--b1)) 90%, transparent);
    color: oklch(var(--bc));
    box-shadow: 0 10px 22px color-mix(in srgb, oklch(var(--bc)) 8%, transparent);
}

.playlist-detail__play-button svg,
.playlist-detail__secondary-button svg,
.playlist-detail__icon-button svg {
    width: 1.05rem;
    height: 1.05rem;
    flex-shrink: 0;
}

.playlist-detail__edit-drawer {
    width: auto;
}

.playlist-detail__drawer-panel {
    width: min(24rem, 92vw);
    min-height: 100%;
    padding: 1.2rem;
    background: oklch(var(--b1));
    color: oklch(var(--bc));
    overflow-y: auto;
}

.playlist-detail__drawer-title {
    margin-bottom: 1rem;
    color: oklch(var(--bc));
    font-size: 1.25rem;
    font-weight: 850;
    letter-spacing: -0.02em;
}

.playlist-detail__field {
    display: grid;
    gap: 0.45rem;
    margin-top: 0.9rem;
}

.playlist-detail__field > span {
    color: color-mix(in oklch, oklch(var(--bc)) 70%, transparent);
    font-size: 0.82rem;
    font-weight: 800;
}

.playlist-detail__upload {
    position: relative;
    min-height: 11rem;
    border-radius: 1rem;
    border: 1px dashed color-mix(in oklch, oklch(var(--bc)) 18%, transparent);
    background: oklch(var(--b2));
    padding: 0.85rem;
    text-align: center;
    overflow: hidden;
}

.playlist-detail__upload-preview {
    max-width: 100%;
    max-height: 14rem;
    border-radius: 0.85rem;
    object-fit: cover;
}

.playlist-detail__upload-action {
    position: absolute;
    width: 1.8rem;
    height: 1.8rem;
    border: 0;
    border-radius: 999px;
    color: oklch(var(--b1));
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: 900;
}

.playlist-detail__upload-action--remove {
    top: -0.45rem;
    right: -0.45rem;
    background: oklch(var(--er));
}

.playlist-detail__upload-action--replace {
    right: -0.45rem;
    bottom: -0.45rem;
    background: oklch(var(--bc));
}

.playlist-detail__upload-empty {
    min-height: 9rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: color-mix(in oklch, oklch(var(--bc)) 62%, transparent);
}

.playlist-detail__upload-empty svg {
    width: 2.6rem;
    height: 2.6rem;
    color: color-mix(in oklch, oklch(var(--bc)) 38%, transparent);
}

.playlist-detail__upload-empty p {
    margin: 0.65rem 0 0.2rem;
    font-size: 0.9rem;
    font-weight: 750;
}

.playlist-detail__upload-empty small {
    color: color-mix(in oklch, oklch(var(--bc)) 48%, transparent);
    font-size: 0.75rem;
}

.playlist-detail__uploading {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    background: color-mix(in oklch, oklch(var(--b1)) 82%, transparent);
}

.playlist-detail__drawer-save {
    width: 100%;
    min-height: 2.7rem;
    margin-top: 1rem;
    border-radius: 999px;
    background: oklch(var(--bc));
    color: oklch(var(--b1));
}

.playlist-detail__recommend-title {
    margin-top: 1.25rem;
    color: color-mix(in oklch, oklch(var(--bc)) 70%, transparent);
    font-size: 0.86rem;
    font-weight: 850;
}

.playlist-detail__recommend-list {
    display: grid;
    gap: 0.55rem;
    margin-top: 0.7rem;
}

.playlist-detail__recommend-row {
    width: 100%;
    border: 0;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.55rem;
    border-radius: 0.95rem;
    background: color-mix(in oklch, oklch(var(--b2)) 70%, transparent);
    color: oklch(var(--bc));
    text-align: left;
}

.playlist-detail__recommend-image {
    width: 3.35rem;
    height: 3.35rem;
    border-radius: 0.8rem;
    object-fit: cover;
    flex-shrink: 0;
}

.playlist-detail__recommend-row span {
    min-width: 0;
}

.playlist-detail__recommend-row strong,
.playlist-detail__recommend-row small {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.playlist-detail__recommend-row small {
    margin-top: 0.2rem;
    color: color-mix(in oklch, oklch(var(--bc)) 54%, transparent);
}

.playlist-detail__menu {
    width: 14rem;
    padding: 0.45rem;
    border-radius: 1rem;
    background: oklch(var(--b1));
    color: oklch(var(--bc));
    box-shadow: 0 18px 42px color-mix(in srgb, oklch(var(--bc)) 16%, transparent);
    z-index: 20;
}

.playlist-detail__tracks {
    max-width: 88rem;
    margin: 1.1rem auto 0;
    padding: 1rem;
    border-radius: 1.35rem;
    background: color-mix(in oklch, oklch(var(--b1)) 90%, transparent);
    box-shadow: 0 14px 34px color-mix(in srgb, oklch(var(--bc)) 8%, transparent);
}

.playlist-detail__tracks-header {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.75rem;
}

.playlist-detail__tracks-header h2 {
    margin: 0;
    color: oklch(var(--bc));
    font-size: 1.35rem;
    font-weight: 850;
    letter-spacing: -0.02em;
}

.playlist-detail__tracks-header p {
    margin: 0.25rem 0 0;
    color: color-mix(in oklch, oklch(var(--bc)) 58%, transparent);
    font-size: 0.88rem;
}

.playlist-detail__tracks-header span {
    flex-shrink: 0;
    min-height: 2rem;
    display: inline-flex;
    align-items: center;
    padding: 0.35rem 0.75rem;
    border-radius: 999px;
    background: oklch(var(--b2));
    color: color-mix(in oklch, oklch(var(--bc)) 70%, transparent);
    font-size: 0.82rem;
    font-weight: 800;
}

.playlist-detail__empty {
    min-height: 15rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    color: color-mix(in oklch, oklch(var(--bc)) 58%, transparent);
}

.playlist-detail__empty-icon {
    width: 4.2rem;
    height: 4.2rem;
    border-radius: 1.35rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: oklch(var(--b2));
}

.playlist-detail__empty-icon svg {
    width: 1.8rem;
    height: 1.8rem;
}

.playlist-detail__empty h2 {
    margin: 1rem 0 0.35rem;
    color: oklch(var(--bc));
    font-size: 1.2rem;
    font-weight: 850;
}

.playlist-detail__empty p {
    margin: 0;
    font-size: 0.9rem;
}

@supports not (color: color-mix(in oklch, white, black)) {
    .playlist-detail {
        background: #f7f7f7;
    }

    .playlist-detail__hero,
    .playlist-detail__tracks,
    .playlist-detail__drawer-panel {
        background: oklch(var(--b1));
    }
}

@media (max-width: 1024px) {
    .playlist-detail {
        height: auto;
        min-height: calc(100vh - 5rem);
        padding: 0.85rem;
    }

    .playlist-detail__hero {
        grid-template-columns: 1fr;
    }

    .playlist-detail__cover-wrap {
        min-height: min(23rem, 62vw);
    }

    .playlist-detail__main {
        padding: 0.25rem 0.2rem 0.35rem;
    }

    .playlist-detail__title {
        max-width: 18ch;
    }
}

@media (max-width: 640px) {
    .playlist-detail {
        padding: 0.75rem 0.35rem 1rem;
    }

    .playlist-detail__hero,
    .playlist-detail__tracks {
        border-radius: 1.25rem;
        padding: 0.8rem;
    }

    .playlist-detail__cover-wrap {
        min-height: 72vw;
    }

    .playlist-detail__title {
        font-size: 2.25rem;
    }

    .playlist-detail__meta-grid {
        grid-template-columns: 1fr;
    }

    .playlist-detail__play-button,
    .playlist-detail__secondary-button {
        flex: 1 1 calc(50% - 0.65rem);
    }

    .playlist-detail__icon-button {
        flex: 0 0 3rem;
    }

    .playlist-detail__tracks-header {
        align-items: flex-start;
        flex-direction: column;
    }
}
</style>
